// frontend/src/pages/MyShelf.js – My Shelf (Saved Storylines)
// Build: 2026-05-26-myshelf-published-cover-layout-v2
//
// Goal:
// - Dedicated page listing the user's saved storylines.
// - NOT social media language. It's a bookshelf.
// - No changes to Storylines logic.
//
// Backend contract supported (current):
// - GET  /api/library/shelf -> { ok: true, items: [{ story, savedAt, storyId }] }
// - POST /api/library/shelf/:storyId/remove -> { ok: true, storyId, saved: false }
//
// Notes:
// - Endpoint-resilient: supports a few fallback response shapes.
// - Requires auth (guests are invited to log in).
// - Prefer shelf-provided storyId for navigation/removal to avoid legacy-id drift.

import React, { useEffect, useMemo, useState } from "react";
import "../App.css";
import { useUser } from "../UserContext";
import { api } from "../api";
import { getPublicationInfo } from "../utils/publicationStatus";

const SHELF_TEXT = {
  en: {
    brand: "NEXTSCENES",
    title: "My Shelf",
    guestSubtitle: "Your Shelf is your private bookshelf. Log in to see what you’ve saved and continue from any device.",
    authedSubtitle: "Your saved Storylines live here. Return to the books you kept, continue reading, or enter the writer’s room when your role allows it.",
    loginToView: "Login to view Shelf",
    loading: "Loading…",
    refresh: "Refresh",
    privateShelf: "private shelf",
    savedStoryline: "saved Storyline",
    savedStorylines: "saved Storylines",
    guestBody: "You are browsing as a guest. Your Shelf belongs to your account, so it can follow you across devices.",
    loadingShelf: "Loading your Shelf…",
    emptyTitle: "Your shelf is waiting.",
    emptyBody: "Saved Storylines appear here.",
    emptyInstruction: "Go to Storylines, choose a story you like, then click",
    saveToShelf: "Save to Shelf",
    exploreStorylines: "Explore Storylines",
    by: "By",
    saved: "Saved",
    created: "Created",
    createdUnknown: "Created: unknown",
    lastUpdated: "Last updated",
    readerView: "Reader View",
    writerRoom: "Writer Room",
    remove: "Remove",
    removeTitle: "Remove from Shelf?",
    removeAbout: "You are about to remove",
    thisStoryline: "this Storyline",
    removeFromPrivate: "from your private Shelf.",
    removeExplain: "This does not delete the Storyline. It will still be available on NextScenes, and you can add it back anytime from the Storylines page.",
    keepOnShelf: "Keep on Shelf",
    removing: "Removing…",
    removeAction: "Remove",
    removeAria: "Remove from shelf confirmation",
    writerAccessError: "Your account does not have Writer access.",
    loginFirst: "Please log in first.",
    storyIdentity: "Story identity",
    savedOnShelf: "Saved on Shelf",
    publishedOnAmazon: "Published on Amazon",
    previewOnNextScenes: "Preview on NextScenes",
    viewOnAmazon: "View on Amazon",
    viewPublishedBook: "View Published Book",
    published: "Published",
  },
  fr: {
    brand: "NEXTSCENES",
    title: "Ma bibliothèque",
    guestSubtitle: "Votre bibliothèque est votre étagère privée. Connectez-vous pour voir ce que vous avez enregistré et continuer depuis n’importe quel appareil.",
    authedSubtitle: "Vos Storylines enregistrées vivent ici. Revenez aux livres que vous avez gardés, continuez la lecture ou entrez dans l’espace d’écriture lorsque votre rôle le permet.",
    loginToView: "Connexion pour voir la bibliothèque",
    loading: "Chargement…",
    refresh: "Actualiser",
    privateShelf: "bibliothèque privée",
    savedStoryline: "Storyline enregistrée",
    savedStorylines: "Storylines enregistrées",
    guestBody: "Vous naviguez comme invité. Votre bibliothèque appartient à votre compte et peut donc vous suivre sur vos appareils.",
    loadingShelf: "Chargement de votre bibliothèque…",
    emptyTitle: "Votre bibliothèque vous attend.",
    emptyBody: "Les Storylines enregistrées apparaissent ici.",
    emptyInstruction: "Allez dans Storylines, choisissez une histoire qui vous plaît, puis cliquez sur",
    saveToShelf: "Enregistrer",
    exploreStorylines: "Explorer les Storylines",
    by: "Par",
    saved: "Enregistré",
    created: "Créé",
    createdUnknown: "Créé : inconnu",
    lastUpdated: "Dernière mise à jour",
    readerView: "Reader View",
    writerRoom: "Writer Room",
    remove: "Retirer",
    removeTitle: "Retirer de la bibliothèque ?",
    removeAbout: "Vous êtes sur le point de retirer",
    thisStoryline: "cette Storyline",
    removeFromPrivate: "de votre bibliothèque privée.",
    removeExplain: "Cela ne supprime pas la Storyline. Elle restera disponible sur NextScenes et vous pourrez l’ajouter de nouveau depuis la page Storylines.",
    keepOnShelf: "Garder dans la bibliothèque",
    removing: "Retrait…",
    removeAction: "Retirer",
    removeAria: "Confirmation de retrait de la bibliothèque",
    writerAccessError: "Votre compte n’a pas accès au rôle Writer.",
    loginFirst: "Veuillez vous connecter d’abord.",
    storyIdentity: "Identité de l’histoire",
    savedOnShelf: "Enregistré",
    publishedOnAmazon: "Publié sur Amazon",
    previewOnNextScenes: "Aperçu sur NextScenes",
    viewOnAmazon: "Voir sur Amazon",
    viewPublishedBook: "Voir le livre publié",
    published: "Publié",
  },
};

function currentShelfLang() {
  try {
    return (window.localStorage.getItem("ns.lang") || "en").toLowerCase().startsWith("fr") ? "fr" : "en";
  } catch {
    return "en";
  }
}

/* =========================
   Tracking (Continue Reading)
   ========================= */

async function fetchTrackingBatch(storyIds) {
  const ids = Array.isArray(storyIds) ? storyIds.map((x) => String(x || "").trim()).filter(Boolean) : [];
  if (ids.length === 0) return {};

  const res = await api("/api/tracking/batch", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ storyIds: ids }),
  });

  if (res && typeof res === "object" && res.summaries && typeof res.summaries === "object") {
    return res.summaries;
  }
  if (res && typeof res === "object") return res;
  return {};
}

function safeStr(v) {
  return typeof v === "string" ? v : "";
}
function safeArray(v) {
  return Array.isArray(v) ? v : [];
}

function getStoryIdFromStory(story) {
  return story?._id || story?.id || "";
}

function normalizeTitleForPublication(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/[’']/g, "")
    .replace(/[^a-z0-9àâçéèêëîïôûùüÿñæœ\s-]/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function isLikely3DMockupUrl(value) {
  const url = String(value || "").trim().toLowerCase();
  return (
    url.includes("3d") ||
    url.includes("mockup") ||
    url.includes("paperback") ||
    url.includes("_sx") ||
    url.includes("_sy") ||
    url.includes("m.media-amazon") ||
    url.includes("m.media amazon") ||
    url.includes("/images/books/") ||
    url.includes("public_site_assets")
  );
}

function getKnownPublicationInfo(story, labels = SHELF_TEXT.en) {
  const lang = currentShelfLang();
  const central = getPublicationInfo(story, lang);
  const title = normalizeTitleForPublication(story?.title || central?.publishedTitle || story?.publishedTitle || story?.publicationTitle);
  const rawStatus = String(story?.publicationStatus || story?.publishingStatus || central?.status || "").trim();
  const explicitUrl = safeStr(
    story?.publicationUrl || story?.amazonUrl || story?.amazonPageUrl || story?.publishedUrl || story?.externalPublicationUrl || central?.amazonHref
  );

  const isPetDog = title.includes("ugo and her pet dog");
  const isButterflyEnglish = title.includes("ugo and the butterfly woman");
  const isButterflyFrench = title.includes("ugo et la femme papillon") || title.includes("ugo et la femme-papillon");
  const isKnownUgoPublished = isPetDog || isButterflyEnglish || isButterflyFrench;
  const isPublished = Boolean(central?.isPublished) || rawStatus === "publishedAmazon" || rawStatus === "published" || isKnownUgoPublished;

  if (!isPublished) {
    return { isPublished: false, publicationLabel: "", amazonHref: "", coverUrl: "", coverUrls: [], publishedAt: "", publishedAuthorName: "" };
  }

  let query = `${safeStr(story?.title || central?.publishedTitle) || "Goddy Oguzie"} Goddy Oguzie`;
  let knownCoverUrls = [];

  if (isPetDog) {
    knownCoverUrls = ["/images/covers/featured-flat/ugo-her-pet-dog.webp", "/images/covers/ugo-and-her-pet-dog.webp"];
    query = "Ugo and Her Pet Dog Goddy Oguzie";
  } else if (isButterflyFrench) {
    knownCoverUrls = ["/images/covers/ugo-femme-papillon.webp"];
    query = "Ugo et la Femme-Papillon Goddy Oguzie";
  } else if (isButterflyEnglish) {
    knownCoverUrls = ["/images/covers/ugo-butterfly-woman.webp"];
    query = "Ugo and the Butterfly Woman Goddy Oguzie";
  }

  const serverCoverUrls = [
    safeStr(central?.coverUrl),
    safeStr(story?.publicationCoverUrl),
    safeStr(story?.publishedCoverUrl),
    safeStr(story?.finalCoverImageUrl),
    safeStr(story?.coverImageUrl),
    safeStr(story?.coverUrl),
    safeStr(story?.coverImage),
    safeStr(story?.imageUrl),
    safeStr(story?.cover),
  ].filter(Boolean);

  const cleanServerCoverUrls = serverCoverUrls.filter((url) => !isLikely3DMockupUrl(url));

  // For known published books, do not let saved shelf/server cover snapshots override
  // the official flat cover. This is what stops old 3D book mockups from returning.
  const coverUrls = Array.from(new Set([
    ...knownCoverUrls,
    ...(knownCoverUrls.length ? [] : cleanServerCoverUrls),
  ].filter(Boolean)));

  return {
    isPublished: true,
    publicationLabel: central?.publicationLabel || labels.publishedOnAmazon || "Published on Amazon",
    amazonHref: explicitUrl || `https://www.amazon.com/s?k=${encodeURIComponent(query)}`,
    coverUrl: coverUrls[0] || "",
    coverUrls,
    publishedAt: safeStr(story?.publishedAt || central?.publishedAt),
    publishedAuthorName: safeStr(central?.publishedAuthorName) || "Goddy Oguzie",
  };
}

function getStoryCoverUrl(story, labels = SHELF_TEXT.en) {
  const candidates = getStoryCoverCandidates(story, labels);
  return candidates[0] || "";
}

function getStoryCoverCandidates(story, labels = SHELF_TEXT.en) {
  const known = getKnownPublicationInfo(story, labels);

  const explicit = safeStr(
    story?.coverImageUrl ||
      story?.coverUrl ||
      story?.coverImage ||
      story?.imageUrl ||
      story?.cover
  ).trim();

  const list = [];

  if (known?.isPublished) {
    if (Array.isArray(known.coverUrls)) list.push(...known.coverUrls);
    if (known.coverUrl) list.push(known.coverUrl);
    if (explicit && !isLikely3DMockupUrl(explicit) && !list.length) list.push(explicit);
  } else {
    if (explicit) list.push(explicit);
    if (Array.isArray(known.coverUrls)) list.push(...known.coverUrls);
    if (known.coverUrl) list.push(known.coverUrl);
  }

  return Array.from(new Set(list.map((x) => String(x || "").trim()).filter(Boolean)));
}

function PublicationMiniStatus({ info, labels = SHELF_TEXT.en }) {
  if (!info?.isPublished) return null;
  return (
    <div className="ns-publication-mini-row ns-myshelf-published-badges" aria-label={info.publicationLabel}>
      <span className="ns-publication-chip is-saved">{labels.savedOnShelf}</span>
      <span className="ns-publication-chip is-published">{info.publicationLabel}</span>
    </div>
  );
}

function formatDateTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString() + " " + d.toLocaleTimeString();
}

function getAuthorInfo(story) {
  const authorObj =
    (story?.authorProfile && typeof story.authorProfile === "object" ? story.authorProfile : null) ||
    (story?.author && typeof story.author === "object" ? story.author : null);

  const authorString = typeof story?.author === "string" ? story.author : "";

  const authorName =
    safeStr(authorObj?.name) || safeStr(story?.authorName) || safeStr(story?.ownerName) || "";

  const authorUsername =
    safeStr(authorObj?.username) ||
    safeStr(story?.authorUsername) ||
    safeStr(story?.ownerUsername) ||
    safeStr(authorString) ||
    safeStr(story?.createdBy) ||
    "";

  const authorId =
    safeStr(authorObj?.id) || safeStr(story?.authorId) || safeStr(story?.ownerId) || "";

  const avatarUrl =
    safeStr(authorObj?.avatarUrl) ||
    safeStr(story?.authorAvatarUrl) ||
    safeStr(story?.authorAvatar) ||
    safeStr(story?.ownerAvatarUrl) ||
    safeStr(story?.ownerAvatar) ||
    "";

  const displayName = authorName || (authorUsername ? authorUsername : "Unknown author");
  const handle = authorUsername ? `@${authorUsername}` : "";

  return { authorName, authorUsername, authorId, avatarUrl, displayName, handle };
}

function getPublishedShelfAuthorName(story, publicationInfo) {
  return (
    safeStr(publicationInfo?.publishedAuthorName) ||
    safeStr(story?.publishedAuthorName) ||
    safeStr(story?.publicationAuthorName) ||
    safeStr(story?.displayAuthorName) ||
    "Goddy Oguzie"
  );
}

function getPublishedShelfGenre(story) {
  const forum = safeStr(story?.forum || story?.genre || story?.category || "").trim();
  if (!forum) return "";
  return forum;
}

function getPublishedShelfTitle(story, publicationInfo) {
  return safeStr(publicationInfo?.publishedTitle || story?.publishedTitle || story?.publicationTitle || story?.title).trim();
}

function forumBandColor(forumRaw) {
  const f = String(forumRaw || "").toLowerCase();
  if (f.includes("mystery")) return "#0ea5e9";
  if (f.includes("crime")) return "#ef4444";
  if (f.includes("fantasy")) return "#a855f7";
  if (f.includes("romance")) return "#f43f5e";
  if (f.includes("poetry")) return "#22c55e";
  if (f.includes("science")) return "#38bdf8";
  if (f.includes("historical")) return "#f59e0b";
  if (f.includes("children")) return "#10b981";
  if (f.includes("non-fiction")) return "#16a34a";
  if (f.includes("humour")) return "#f97316";
  if (f.includes("thriller") || f.includes("suspense")) return "#111827";
  return "#0f766e";
}

function ShelfFallbackPlate({ story, labels = SHELF_TEXT.en }) {
  const title = safeStr(story?.title).trim() || "Untitled Story";
  const author = getAuthorInfo(story);
  const band = forumBandColor(story?.forum);
  const forumLabel = safeStr(story?.forum).trim().replace(/\s+Forum$/i, "").trim();
  const modeLabel = safeStr(story?.writingMode).trim();

  return (
    <div
      className="ns-story-identity-plate"
      aria-label={labels.storyIdentity}
      style={{
        width: 142,
        minWidth: 142,
        height: 204,
        borderRadius: 16,
        overflow: "hidden",
        background: "linear-gradient(180deg, rgba(15,23,42,0.96) 0%, rgba(15,23,42,0.90) 100%)",
        border: "1px solid rgba(15,23,42,0.10)",
        boxShadow: "0 10px 24px rgba(2,6,23,0.12)",
        display: "flex",
        flexDirection: "column",
      }}
      title={title}
    >
      <div style={{ height: 12, background: band }} />
      <div style={{ padding: 12, display: "flex", flexDirection: "column", gap: 8 }}>
        <div
          style={{
            fontWeight: 900,
            color: "#ffffff",
            fontSize: 11.5,
            lineHeight: "14px",
            letterSpacing: 0.1,
            textTransform: "uppercase",
            opacity: 0.96,
          }}
        >
          {title.length > 42 ? title.slice(0, 42) + "…" : title}
        </div>

        <div style={{ color: "rgba(255,255,255,0.75)", fontSize: 11, lineHeight: "13px" }}>
          {author.displayName}
        </div>

        {(forumLabel || modeLabel) && (
          <div style={{ marginTop: 6, color: "rgba(255,255,255,0.68)", fontSize: 11, lineHeight: "13px" }}>
            {forumLabel ? forumLabel : "Story"}
            {modeLabel ? <span> • {modeLabel}</span> : null}
          </div>
        )}
      </div>

      <div style={{ marginTop: "auto", padding: 10 }}>
        <div
          style={{
            height: 32,
            borderRadius: 12,
            background: "rgba(255,255,255,0.08)",
            border: "1px solid rgba(255,255,255,0.10)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "rgba(255,255,255,0.80)",
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: 0.1,
          }}
        >
          {labels.savedOnShelf}
        </div>
      </div>
    </div>
  );
}

function StoryIdentityPlateInline({ story, labels = SHELF_TEXT.en }) {
  const title = safeStr(story?.title).trim() || "Untitled Story";
  const coverCandidates = getStoryCoverCandidates(story, labels);
  const [coverIndex, setCoverIndex] = useState(0);
  const coverUrl = coverCandidates[coverIndex] || "";
  const publicationInfo = getKnownPublicationInfo(story, labels);
  const isPublished = Boolean(publicationInfo?.isPublished);

  if (!coverUrl) {
    return <ShelfFallbackPlate story={story} labels={labels} />;
  }

  return (
    <div
      className={`ns-story-identity-cover ns-myshelf-cover-frame${isPublished ? " is-published-book" : ""}`}
      aria-label={labels.storyIdentity}
      title={title}
    >
      <img
        src={coverUrl}
        alt={title}
        loading="lazy"
        className="ns-myshelf-cover-img"
        onError={() => {
          setCoverIndex((idx) => idx + 1);
        }}
      />
    </div>
  );
}

function AuthorAvatar({ story }) {
  const author = getAuthorInfo(story);

  if (author.avatarUrl) {
    return <img src={author.avatarUrl} alt={author.displayName} className="ns-storylines-avatar" />;
  }

  return (
    <div
      className="ns-storylines-avatar ns-storylines-avatar-fallback"
      title={author.displayName}
      aria-label={author.displayName}
    >
      <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" focusable="false" style={{ opacity: 0.85 }}>
        <path
          fill="currentColor"
          d="M12 12a4 4 0 1 0-4-4a4 4 0 0 0 4 4m0 2c-4.42 0-8 2.24-8 5v1h16v-1c0-2.76-3.58-5-8-5"
        />
      </svg>
    </div>
  );
}

function normalizeShelfResponse(data) {
  if (data && Array.isArray(data.items)) {
    const items = data.items
      .map((it) => {
        const story = it?.story && typeof it.story === "object" ? it.story : null;
        if (!story) return null;

        const storyId = String(it?.storyId || getStoryIdFromStory(story) || "").trim();
        if (!storyId) return null;

        const savedAt = it?.savedAt ? String(it.savedAt) : "";
        return { story, storyId, savedAt };
      })
      .filter(Boolean);

    return { items };
  }

  if (Array.isArray(data)) {
    const items = data
      .map((story) => {
        const sid = getStoryIdFromStory(story);
        if (!sid) return null;
        return { story, storyId: String(sid), savedAt: "" };
      })
      .filter(Boolean);

    return { items };
  }

  const candidate =
    (Array.isArray(data?.stories) ? data.stories : null) ||
    (Array.isArray(data?.shelf) ? data.shelf : null) ||
    (Array.isArray(data?.items) ? data.items : null) ||
    null;

  if (candidate) {
    const items = candidate
      .map((x) => {
        const story = x?.story && typeof x.story === "object" ? x.story : x;
        const sid = String(x?.storyId || getStoryIdFromStory(story) || "").trim();
        if (!sid) return null;
        const savedAt = x?.savedAt ? String(x.savedAt) : "";
        return { story, storyId: sid, savedAt };
      })
      .filter(Boolean);

    return { items };
  }

  return { items: [] };
}

function normalizeStoriesListResponse(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.stories)) return payload.stories;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
}

function mergeAuthorProfileIntoStory(story, fresh) {
  if (!story || !fresh) return story;
  const freshProfile = fresh.authorProfile && typeof fresh.authorProfile === "object" ? fresh.authorProfile : {};
  const freshAvatar =
    safeStr(freshProfile.avatarUrl) ||
    safeStr(fresh.authorAvatarUrl) ||
    safeStr(fresh.authorAvatar) ||
    safeStr(fresh.ownerAvatarUrl) ||
    "";

  const freshName = safeStr(freshProfile.name) || safeStr(fresh.authorName) || safeStr(fresh.ownerName) || "";
  const freshUsername =
    safeStr(freshProfile.username) ||
    safeStr(fresh.authorUsername) ||
    safeStr(fresh.ownerUsername) ||
    safeStr(fresh.createdBy) ||
    "";
  const freshId = safeStr(freshProfile.id) || safeStr(fresh.authorId) || safeStr(fresh.ownerId) || "";

  return {
    ...story,
    authorName: freshName || story.authorName,
    authorUsername: freshUsername || story.authorUsername,
    authorId: freshId || story.authorId,
    authorAvatarUrl: freshAvatar || story.authorAvatarUrl,
    authorProfile: {
      ...(story.authorProfile && typeof story.authorProfile === "object" ? story.authorProfile : {}),
      ...(freshProfile || {}),
      id: freshId || safeStr(story?.authorProfile?.id) || safeStr(story?.authorId),
      username: freshUsername || safeStr(story?.authorProfile?.username) || safeStr(story?.authorUsername),
      name: freshName || safeStr(story?.authorProfile?.name) || safeStr(story?.authorName),
      avatarUrl: freshAvatar || safeStr(story?.authorProfile?.avatarUrl) || safeStr(story?.authorAvatarUrl),
    },
  };
}

async function enrichShelfItemsWithFreshStories(items) {
  const rows = safeArray(items).filter((it) => it && it.story && it.storyId);
  if (!rows.length) return rows;

  try {
    const payload = await api("/api/stories", { method: "GET" });
    const freshStories = normalizeStoriesListResponse(payload);
    if (!freshStories.length) return rows;

    const byId = new Map();
    freshStories.forEach((story) => {
      const id = String(getStoryIdFromStory(story) || "").trim();
      if (id) byId.set(id, story);
    });

    return rows.map((item) => {
      const sid = String(item?.storyId || getStoryIdFromStory(item?.story) || "").trim();
      const fresh = sid ? byId.get(sid) : null;
      if (!fresh) return item;
      return { ...item, story: mergeAuthorProfileIntoStory(item.story, fresh) };
    });
  } catch {
    return rows;
  }
}

async function tryGetShelfList() {
  const attempts = ["/api/library/shelf", "/api/library/shelf/list", "/api/library/shelf/my"];

  let lastErr = null;
  for (const url of attempts) {
    try {
      const data = await api(url, { method: "GET" });
      const normalized = normalizeShelfResponse(data);
      return { ok: true, urlUsed: url, items: normalized.items };
    } catch (e) {
      lastErr = e;
    }
  }

  return {
    ok: false,
    message: "Shelf list endpoint not found. Backend must provide GET /api/library/shelf (recommended).",
    error: lastErr,
  };
}

async function tryRemoveFromShelf(storyId) {
  const id = String(storyId || "").trim();
  if (!id) throw new Error("Missing story id.");

  const attempts = [
    { method: "POST", url: `/api/library/shelf/${encodeURIComponent(id)}/remove` },
    { method: "DELETE", url: `/api/library/shelf/${encodeURIComponent(id)}` },
    { method: "POST", url: `/api/library/shelf/${encodeURIComponent(id)}/unsave` },
    {
      method: "POST",
      url: `/api/library/shelf/${encodeURIComponent(id)}/toggle`,
      body: JSON.stringify({ saved: false }),
    },
  ];

  let lastErr = null;

  for (const a of attempts) {
    try {
      const opts = { method: a.method };
      if (a.body) opts.body = a.body;
      const data = await api(a.url, opts);
      return { ok: true, data, urlUsed: a.url };
    } catch (e) {
      lastErr = e;
    }
  }

  const msg = String(lastErr?.message || "Failed to remove from shelf.");
  throw new Error(msg);
}

function ConfirmRemoveModal({ open, title, onKeep, onRemove, busy, labels = SHELF_TEXT.en }) {
  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={labels.removeAria}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) onKeep();
      }}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        background: "rgba(2,6,23,0.55)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
    >
      <div
        className="ns-card"
        style={{
          width: "min(520px, 100%)",
          borderRadius: 16,
          boxShadow: "0 18px 60px rgba(2,6,23,0.28)",
        }}
      >
        <div style={{ padding: 16 }}>
          <div style={{ fontWeight: 900, fontSize: 16, marginBottom: 8 }}>{labels.removeTitle}</div>

          <div className="ns-card-meta" style={{ marginBottom: 12, lineHeight: "18px" }}>
            {labels.removeAbout}{" "}
            <span style={{ fontWeight: 900, color: "#0f172a" }}>
              {title ? `“${title}”` : labels.thisStoryline}
            </span>{" "}
            {labels.removeFromPrivate}
          </div>

          <div className="ns-card-meta" style={{ lineHeight: "18px" }}>
            {labels.removeExplain}
          </div>

          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 16, flexWrap: "wrap" }}>
            <button type="button" className="ns-button ns-button-outline" onClick={onKeep} disabled={busy}>
              {labels.keepOnShelf}
            </button>
            <button type="button" className="ns-button ns-button-primary" onClick={onRemove} disabled={busy}>
              {busy ? labels.removing : labels.removeAction}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function MyShelf({ onNavigatePage }) {
  const { isAuthenticated, isWriter, isAdmin } = useUser();
  const lang = currentShelfLang();
  const labels = SHELF_TEXT[lang] || SHELF_TEXT.en;

  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [items, setItems] = useState([]);
  const [trackingByStoryId, setTrackingByStoryId] = useState({});

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmStoryId, setConfirmStoryId] = useState("");
  const [confirmTitle, setConfirmTitle] = useState("");

  const canWrite = Boolean(isAuthenticated && (isWriter || isAdmin));

  function bumpShelfSyncStamp() {
    try {
      window.localStorage.setItem("ns.shelf.changedAt", String(Date.now()));
    } catch {}
  }

  function goStorylines() {
    if (onNavigatePage) {
      onNavigatePage("storylines");
      return;
    }
    window.location.href = "/storylines";
  }

  async function loadShelf() {
    if (!isAuthenticated) {
      setLoading(false);
      setItems([]);
      setErr("");
      return;
    }

    setLoading(true);
    setErr("");

    const res = await tryGetShelfList();
    if (!res.ok) {
      setItems([]);
      setErr(res.message || "Failed to load shelf.");
      setLoading(false);
      return;
    }

    const clean = safeArray(res.items).filter((it) => it && it.story && it.storyId);
    const enriched = await enrichShelfItemsWithFreshStories(clean);
    setItems(enriched);
    setLoading(false);
  }

  useEffect(() => {
    loadShelf();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  useEffect(() => {
    (async () => {
      const ids = safeArray(items)
        .map((it) => String(it?.storyId || getStoryIdFromStory(it?.story) || "").trim())
        .filter(Boolean);

      if (ids.length === 0) {
        setTrackingByStoryId({});
        return;
      }

      try {
        const summaries = await fetchTrackingBatch(ids.slice(0, 350));
        if (summaries && typeof summaries === "object") setTrackingByStoryId(summaries);
        else setTrackingByStoryId({});
      } catch {
        setTrackingByStoryId({});
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length]);

  function goLogin() {
    try {
      window.localStorage.setItem("ns.auth.intent", "myshelf_login_required");
    } catch {}
    if (onNavigatePage) onNavigatePage("login");
    else alert(labels.loginFirst);
  }

  function openReader(storyId, story) {
    const id = String(storyId || getStoryIdFromStory(story) || "").trim();
    if (!id) return;

    try {
      window.localStorage.setItem("ns.selectedStoryId", String(id));
      window.localStorage.setItem("ns.reader.focusStoryId", String(id));
    } catch {}

    if (onNavigatePage) {
      onNavigatePage("readerView", { storyId: id });
      return;
    }

    window.location.href = `/reader-view?storyId=${encodeURIComponent(id)}`;
  }

  function openWriter(storyId, story) {
    if (!onNavigatePage) return;

    const id = String(storyId || getStoryIdFromStory(story) || "").trim();
    if (!id) return;

    if (!isAuthenticated) return goLogin();
    if (!canWrite) {
      alert(labels.writerAccessError);
      return;
    }

    try {
      window.localStorage.setItem("ns.selectedStoryId", String(id));
      window.localStorage.setItem("ns.writerStudio.focusStoryId", String(id));
      window.localStorage.setItem("ns.writerStudio.storyId", String(id));
    } catch {}

    onNavigatePage("writerStudio");
  }

  function askRemoveShelfItem(storyId, story) {
    const id = String(storyId || "").trim();
    if (!id) return;
    if (!isAuthenticated) return goLogin();
    if (busy) return;

    setConfirmStoryId(id);
    setConfirmTitle(safeStr(story?.title).trim());
    setConfirmOpen(true);
  }

  function closeConfirmKeep() {
    if (busy) return;
    setConfirmOpen(false);
    setConfirmStoryId("");
    setConfirmTitle("");
  }

  async function confirmRemoveNow() {
    const id = String(confirmStoryId || "").trim();
    if (!id) {
      closeConfirmKeep();
      return;
    }

    setBusy(true);
    setErr("");

    try {
      const res = await tryRemoveFromShelf(id);
      const returnedId = String(res?.data?.storyId || "").trim();

      setItems((prev) =>
        prev.filter((it) => {
          const key = String(it?.storyId || "").trim();
          if (!key) return false;
          if (key === id) return false;
          if (returnedId && key === returnedId) return false;
          return true;
        })
      );

      bumpShelfSyncStamp();

      setConfirmOpen(false);
      setConfirmStoryId("");
      setConfirmTitle("");
    } catch (e) {
      setErr(String(e?.message || "Failed to remove from shelf."));
      setConfirmOpen(false);
      setConfirmStoryId("");
      setConfirmTitle("");
    } finally {
      setBusy(false);
    }
  }

  const sorted = useMemo(() => {
    const arr = [...safeArray(items)];

    arr.sort((a, b) => {
      const sa = String(a?.savedAt || "");
      const sb = String(b?.savedAt || "");
      if (sa && sb) return sb.localeCompare(sa);
      if (sa && !sb) return -1;
      if (!sa && sb) return 1;

      const da = String(a?.story?.updatedAt || a?.story?.createdAt || "");
      const db = String(b?.story?.updatedAt || b?.story?.createdAt || "");
      return db.localeCompare(da);
    });

    return arr;
  }, [items]);

  return (
    <div className="ns-page">
      <ConfirmRemoveModal
        open={confirmOpen}
        title={confirmTitle}
        onKeep={closeConfirmKeep}
        onRemove={confirmRemoveNow}
        busy={busy}
        labels={labels}
      />

      <header
        className="ns-identity-hero ns-myshelf-hero"
        style={{
          backgroundImage: `linear-gradient(90deg, rgba(4, 31, 24, 0.94) 0%, rgba(4, 47, 34, 0.84) 48%, rgba(4, 31, 24, 0.68) 100%), url(${process.env.PUBLIC_URL}/images/storyhub/storyhub-background.png)`,
        }}
      >
        <div className="ns-identity-hero-copy">
          <p className="ns-identity-kicker" style={{ color: "#D9A441", borderColor: "rgba(217, 164, 65, 0.52)", background: "rgba(217, 164, 65, 0.10)", textShadow: "0 1px 10px rgba(217, 164, 65, 0.18)" }}>{labels.brand}</p>
          <h1 className="ns-identity-title">{labels.title}</h1>

          {!isAuthenticated ? (
            <p className="ns-identity-subtitle">
              {labels.guestSubtitle}
            </p>
          ) : (
            <p className="ns-identity-subtitle">
              {labels.authedSubtitle}
            </p>
          )}

          <div className="ns-identity-actions">
            {!isAuthenticated ? (
              <button className="ns-button ns-button-outline" type="button" onClick={goLogin}>
                {labels.loginToView}
              </button>
            ) : (
              <>
                <button className="ns-button ns-button-outline" type="button" onClick={loadShelf} disabled={loading || busy}>
                  {loading ? labels.loading : labels.refresh}
                </button>
    <span className="ns-card-meta ns-identity-status">
      {sorted.length === 1 ? `1 ${labels.savedStoryline}` : `${sorted.length} ${labels.savedStorylines}`}
    </span>
  </>
)}

{err ? <span className="ns-card-meta ns-identity-status is-error">{err}</span> : null}
          </div>
        </div>

        <div className="ns-identity-hero-stat" aria-label="Shelf summary">
          <span className="ns-identity-stat-number">{isAuthenticated ? sorted.length : 0}</span>
          <span className="ns-identity-stat-label">
            {isAuthenticated ? (sorted.length === 1 ? labels.savedStoryline : labels.savedStorylines) : labels.privateShelf}
          </span>
        </div>
      </header>

      <section className="ns-column">
        <div className="ns-myshelf-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(390px, 1fr))", gap: 22, alignItems: "stretch" }}>
          {!isAuthenticated ? (
            <div className="ns-card">
              <p className="ns-card-meta">
                {labels.guestBody}
              </p>
            </div>
          ) : null}

          {isAuthenticated && loading ? (
            <div className="ns-card">
              <p className="ns-card-meta">{labels.loadingShelf}</p>
            </div>
          ) : null}

          {isAuthenticated && !loading && sorted.length === 0 ? (
            <div className="ns-card" style={{ padding: 18 }}>
              <p style={{ marginBottom: 6, fontWeight: 900, fontSize: 16 }}>{labels.emptyTitle}</p>
              <p className="ns-card-meta" style={{ lineHeight: "18px" }}>
                {labels.emptyBody}
                <br />
                {labels.emptyInstruction} <b>{labels.saveToShelf}</b>.
              </p>

              <div style={{ marginTop: 12, display: "flex", gap: 10, flexWrap: "wrap" }}>
                <button className="ns-button ns-button-primary" type="button" onClick={goStorylines}>
                  {labels.exploreStorylines}
                </button>
              </div>
            </div>
          ) : null}

          {sorted.map((it) => {
            const story = it?.story || null;
            if (!story) return null;

            const id = String(it?.storyId || getStoryIdFromStory(story) || "").trim();
            if (!id) return null;

            const t = trackingByStoryId?.[String(id)] || null;
            const hasProgress = !!t?.lastReadAt;
            const isUpdated = !!t?.updated;
            const publicationInfo = getKnownPublicationInfo(story, labels);
            const isPublishedBook = Boolean(publicationInfo?.isPublished);
            const author = getAuthorInfo(story);
            const publishedAuthorName = getPublishedShelfAuthorName(story, publicationInfo);
            const publishedGenre = getPublishedShelfGenre(story);
            const publishedTitle = getPublishedShelfTitle(story, publicationInfo);
            const readerPrimaryLabel = isPublishedBook ? labels.viewPublishedBook : labels.readerView;
            const readerHint = !isPublishedBook && hasProgress ? (isUpdated ? "Continue · Updated" : "Continue") : "";

            const createdAt = story?.createdAt ? String(story.createdAt) : "";
            const updatedAt = story?.updatedAt ? String(story.updatedAt) : "";
            const savedAt = it?.savedAt ? String(it.savedAt) : "";

            return (
              <article key={id} className="ns-card storyline-card ns-myshelf-card" style={{ display: "flex", flexDirection: "column", justifyContent: "space-between", minHeight: 310, borderRadius: 24, overflow: "hidden" }}>
                <div className="storyline-card-main" style={{ display: "grid", gridTemplateColumns: "164px minmax(0, 1fr)", gap: 18, alignItems: "start" }}>
                  <div className="storyline-cover-slot" aria-label={labels.storyIdentity}>
                    <StoryIdentityPlateInline story={story} labels={labels} />
                  </div>
                  <div className="storyline-card-text ns-myshelf-card-text" style={{ minWidth: 0 }}>
                    {isPublishedBook ? (
                      <div className="ns-myshelf-published-info">
                        {publishedTitle ? (
                          <div className="ns-myshelf-published-title">{publishedTitle}</div>
                        ) : null}

                        <div className="ns-myshelf-published-author">
                          <span>{labels.by} </span>
                          <strong>{publishedAuthorName}</strong>
                        </div>

                        {publishedGenre ? (
                          <div className="ns-myshelf-published-genre">{publishedGenre}</div>
                        ) : null}

                        <PublicationMiniStatus info={publicationInfo} labels={labels} />
                      </div>
                    ) : (
                      <>
                        <div className="storyline-byline" style={{ marginTop: 2 }}>
                          <AuthorAvatar story={story} />
                          <div className="storyline-byline-text">
                            <div className="storyline-author-name">
                              <span style={{ fontWeight: 800 }}>{labels.by} </span>
                              {author.authorUsername ? (
                                <button
                                  type="button"
                                  className="ns-link-button"
                                  title={`View ${author.displayName}'s profile`}
                                  onClick={() => window.open(`/u/${author.authorUsername}`, "_blank", "noopener,noreferrer")}
                                  style={{
                                    background: "none",
                                    border: "none",
                                    padding: 0,
                                    margin: 0,
                                    cursor: "pointer",
                                    fontWeight: 900,
                                    color: "#0f172a",
                                    textDecoration: "underline",
                                  }}
                                >
                                  {author.displayName}
                                </button>
                              ) : (
                                <span>{author.displayName}</span>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="storyline-dates" style={{ marginTop: 10 }}>
                          {savedAt ? <div className="ns-card-meta">{labels.saved}: {formatDateTime(savedAt)}</div> : null}

                          {createdAt ? (
                            <div className="ns-card-meta">{labels.created}: {formatDateTime(createdAt)}</div>
                          ) : (
                            <div className="ns-card-meta" style={{ opacity: 0.75 }}>
                              {labels.createdUnknown}
                            </div>
                          )}

                          {updatedAt ? <div className="ns-card-meta">{labels.lastUpdated}: {formatDateTime(updatedAt)}</div> : null}
                        </div>
                      </>
                    )}
                  </div>

                </div>

                <div className="storyline-card-actions" style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", paddingTop: 14, marginTop: 14, borderTop: "1px solid rgba(15, 23, 42, 0.08)" }}>
                  <button type="button" className="ns-button ns-button-outline" style={{ height: 28, padding: "0 10px", fontSize: 11.5, borderRadius: 999 }} onClick={() => {
                    try {
                      if (hasProgress) {
                        window.localStorage.setItem("ns.reader.autoOpenValidated", "1");
                        window.localStorage.setItem("ns.reader.autoOpenStoryId", String(id));
                      }
                    } catch {}
                    openReader(id, story);
                  }} disabled={busy}>
                    {readerPrimaryLabel}
                  </button>


                  {readerHint ? (
                    <button
                      type="button"
                      className="ns-button ns-button-outline"
                      onClick={(e) => {
                        e.stopPropagation();
                        try {
                          window.localStorage.setItem("ns.reader.autoOpenValidated", "1");
                          window.localStorage.setItem("ns.reader.autoOpenStoryId", String(id));
                        } catch {}
                        openReader(id, story);
                      }}
                      disabled={busy}
                      title={readerHint}
                      style={{
                        height: 28,
                        padding: "0 10px",
                        borderRadius: 999,
                        border: "1px solid rgba(22, 163, 74, 0.45)",
                        background: "rgba(22, 163, 74, 0.10)",
                        color: "rgba(21, 128, 61, 0.98)",
                        fontWeight: 900,
                        fontSize: 11.5,
                        letterSpacing: 0.1,
                        boxShadow: "0 1px 0 rgba(0,0,0,0.02)",
                      }}
                    >
                      {readerHint}
                    </button>
                  ) : null}


                  {publicationInfo.isPublished && publicationInfo.amazonHref ? (
                    <button
                      type="button"
                      className="ns-button ns-button-outline ns-amazon-action"
                      style={{ height: 28, padding: "0 10px", fontSize: 11.5, borderRadius: 999 }}
                      onClick={() => window.open(publicationInfo.amazonHref, "_blank", "noopener,noreferrer")}
                      disabled={busy}
                    >
                      {labels.viewOnAmazon}
                    </button>
                  ) : null}

                  {canWrite && !isPublishedBook ? (
                    <button type="button" className="ns-button ns-button-primary" style={{ height: 32, padding: "0 10px", fontSize: 12 }} onClick={() => openWriter(id, story)} disabled={busy}>
                      {labels.writerRoom}
                    </button>
                  ) : null}

                  <button
                    type="button"
                    className="ns-button ns-button-outline"
                    style={{ height: 28, padding: "0 10px", fontSize: 11.5, borderRadius: 999 }}
                    onClick={() => askRemoveShelfItem(id, story)}
                    disabled={busy}
                    title={labels.remove}
                  >
                    {labels.remove}
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      </section>
    </div>
  );
}
